/* eslint-disable import/no-extraneous-dependencies */
import { useContext, useEffect, useMemo } from "react";
import {
  useGetAllFiledTeams,
  useGetAllUsers,
  useGetUsersByRole,
  useGetVehicles,
} from "utils/apis/lookups.api";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import {
  Button,
  Combobox,
  Input,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  Form,
} from "@repo/ui";
import { useForm } from "react-hook-form";
import moment from "moment";
import { UserContext } from "utils/reducers/user";
import { useEditDailyOperationRosters } from "utils/apis/store.api";

function EditRoster({ route, onRouteUpdated }) {
  const defaultValues = {
    rosterId: "",
    teamLeaderId: "",
    driverId: "",
    guard1: "",
    guard2: "",
    guard3: "",
    busTime: new Date(),
    routeTime: new Date(),
    cashBoxSerial: "",
    hhtSerial: "",
    printerSerial: "",
    requestedBy: "",
    remarks: "",
  };
  const {
    mutate: editRoster,
    isSuccess: editSuccess,
    isError: editError,
  } = useEditDailyOperationRosters();
  const user = useContext(UserContext);

  const { data: teamLeaders } = useGetUsersByRole(
    user.CashCenterId,
    "Team Leader",
  );
  const { data: fieldTeams } = useGetAllFiledTeams(user.CashCenterId, "Guard");
  const { data: drivers } = useGetUsersByRole(user.CashCenterId, "Driver");
  const { data: users } = useGetAllUsers();
  const { data: vehicles } = useGetVehicles(user.CashCenterId);
  let _busTime = "";
  let _routeTime = "";
  if (route?.busTime) {
    const _time = route.busTime.split("T").join(" ").replace("Z", "");

    _busTime = moment(_time, "YYYY-MM-DD HH:mm:ss").toDate();
  }

  if (route?.routeTime) {
    const _time = route.routeTime.split("T").join(" ").replace("Z", "");
    _routeTime = moment(_time, "YYYY-MM-DD HH:mm:ss").toDate();
  }

  const _guards = route?.fieldTeam.filter((elm) => elm.role === "Guard") || [];
  const teamMembers = {
    teamLeaderId:
      route?.fieldTeam.find((elm) => elm.role === "Team Leader")?.employeeId ||
      "",
    driverId:
      route?.fieldTeam.find((elm) => elm.role === "Driver")?.employeeId || "",
    guard1: _guards?.length ? _guards[0].employeeId : "",
    guard2: _guards?.length > 1 ? _guards[1].employeeId : "",
    guard3: _guards?.length > 2 ? _guards[2].employeeId : "",
  };
  const _routeDefault = {
    ...defaultValues,
    ...route,
    busTime: route?.busTime ? moment(_busTime).toDate() : null,
    routeTime: route?.routeTime ? moment(_routeTime).toDate() : null,
    ...teamMembers,
  };
  const form = useForm({
    defaultValues: { ..._routeDefault },
  });

  const onBtnUpdateRouteClicked = () => {
    const _data = form.getValues();
    let busTime = moment().format("[1001-01-01T]HH:mm:ss[Z]");
    if (_data.busTime) {
      busTime = moment(_data.busTime).format("[1001-01-01T]HH:mm:ss[Z]");
    }
    let routeTime = moment().format("[1001-01-01T]HH:mm:ss[Z]");
    if (_data.routeTime) {
      routeTime = moment(_data.routeTime).format("[1001-01-01T]HH:mm:ss[Z]");
    }
    editRoster({
      rosterId: _data.rosterId || "",
      teamLeaderId: _data.teamLeaderId || "",
      driverId: _data.driverId || "",
      guard1: _data.guard1 || "",
      guard2: _data.guard2 || "",
      guard3: _data.guard3 || "",
      busTime,
      routeTime,
      cashBoxSerial: _data.cashBoxSerial || "",
      hhtSerial: _data.hhtSerial || "",
      printerSerial: _data.printerSerial || "",
      requestedBy: _data.requestedBy || "",
      remarks: _data.remarks || "",
    });
  };
  useEffect(() => {
    if (editSuccess) {
      onRouteUpdated();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSuccess, editError]);

  const editPermissions = {
    "Operations Supervisor": [
      "busTime",
      "routeTime",
      "teamLeaderId",
      "driverId",
      "guard1",
      "guard2",
      "guard3",
      "cashBoxSerial",
      "remarks",
    ],
    "P&A Assistant": ["All"],
    "P&A Supervisor": ["All"],
  };
  const editableFields = useMemo(() => {
    const allowedUsers = Object.keys(editPermissions);
    let allowedFields = [];

    allowedUsers.forEach((elm) => {
      if (user.Roles.includes(elm)) {
        allowedFields = editPermissions[elm];
      }
    });

    return allowedFields;
  }, [user]);

  const isFieldEditable = (field) => {
    return editableFields.includes(field) || editableFields.includes("All");
  };

  return (
    <>
      <Form {...form}>
        <div className="flex flex-row gap-2">
          <div className="w-full">
            <FormInput form={form} name="routeNo" label="Route No" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="area" label="Area" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="region" label="Region" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="status" label="Status" disabled />
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormInput
              form={form}
              name="vanRegistrationNo"
              label="Vehicle"
              disabled
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="cashBoxSerial"
              label="Cash Box Serial"
              disabled={!isFieldEditable("cashBoxSerial")}
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="hhtSerial"
              label="HHT Serial"
              disabled={!isFieldEditable("hhtSerial")}
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="printerSerial"
              label="printer Serial"
              disabled={!isFieldEditable("printerSerial")}
            />
          </div>
        </div>
        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormCombobox
              form={form}
              name="teamLeaderId"
              label="Team Leader"
              disabled={!isFieldEditable("teamLeaderId")}
              data={fieldTeams?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="driverId"
              label="Driver"
              disabled={!isFieldEditable("driverId")}
              data={fieldTeams?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard1"
              label="Custodian"
              disabled={!isFieldEditable("guard1")}
              data={fieldTeams?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard2"
              label="Guard 1"
              disabled={!isFieldEditable("guard2")}
              data={fieldTeams?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard3"
              label="Guard 2"
              disabled={!isFieldEditable("guard3")}
              data={fieldTeams?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="requestedBy"
              label="RequestedBy"
              disabled={!isFieldEditable("requestedBy")}
              data={users?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormField
              control={form.control}
              name="busTime"
              rules={{ required: "Please select time" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bus Time</FormLabel>
                  <TimePicker
                    value={field.value}
                    disabled={!isFieldEditable("busTime")}
                    onChange={(e) => {
                      field.onChange(e?.value || null);
                    }}
                    format="HH:mm" // Use "hh:mm a" for 12-hour clock
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-full">
            <FormField
              control={form.control}
              name="routeTime"
              rules={{ required: "Please select time" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Route Time</FormLabel>
                  <TimePicker
                    value={field.value}
                    disabled={!isFieldEditable("routeTime")}
                    onChange={(e) => {
                      field.onChange(e?.value || null);
                    }}
                    format="HH:mm" // Use "hh:mm a" for 12-hour clock
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="w-full ">
          <FormField
            control={form.control}
            name="remarks"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comments</FormLabel>
                <Textarea
                  className="w-full"
                  value={field.value}
                  disabled={!isFieldEditable("remarks")}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>

      <div className="border-b my-3 bg-accent" />
      <Button
        variant="secondary"
        onClick={onBtnUpdateRouteClicked}
        className="ml-auto flex w-[140px]"
      >
        Update Roster
      </Button>
    </>
  );
}

export default EditRoster;
function FormCombobox({
  form,
  name,
  label,
  placeholder,
  data,
  rules,
  onChange,
  disabled,
  ...props
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Combobox
            value={field.value}
            setValue={(e) => {
              field.onChange(e);
              onChange?.(e);
            }}
            data={data}
            placeholder={placeholder}
            disabled={disabled}
            {...props}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function FormInput({
  form,
  name,
  label,
  placeholder,
  rules,
  data,
  onChange,
  disabled,
  ...props
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Input
            value={field.value}
            placeholder={placeholder}
            onChange={(e) => {
              field.onChange(e);
              onChange?.(e);
            }}
            disabled={disabled}
            {...props}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
